import {
    BrandColorDefinitions,
    SettingsDocument,
    ThemeColorDefinitions,
    normalizeSettingsDocument,
    themeRefName,
} from '../api/models/configuration.model'

/** Resolved colour values, keyed by the colour key. */
export type BrandColors = Record<string, string>

export type ThemeMode = 'light' | 'dark'

export interface ResolvedScope {
    /** Theme colours in use, keyed by theme name. */
    theme: BrandColors
    /** Element colours in use, keyed by colour key. */
    elements: BrandColors
}

export interface BrandTheme {
    light: ResolvedScope
    dark: ResolvedScope
    /** The document as stored. The editor edits this and re-resolves. */
    saved: SettingsDocument
}

/**
 * Turns the values stored for a brand into the colours the pages actually use.
 *
 * Two passes: the theme colours first, then the element colours, which mostly just point at a
 * theme colour. Only what somebody chose is stored, so a reference keeps following the theme
 * instead of being frozen at whatever the theme happened to be when it was saved.
 */
export function resolveBrandTheme(saved: SettingsDocument): BrandTheme {
    const document = normalizeSettingsDocument(saved)
    const light = resolveScope(document, 'light', null)
    const dark = resolveScope(document, 'dark', light)

    return { light, dark, saved: document }
}

/** Every CSS variable of a scope, theme colours included so the pages can use them directly. */
export function cssVariablesOf(scope: ResolvedScope): BrandColors {
    const variables: BrandColors = {}

    ThemeColorDefinitions.forEach(definition => {
        const value = scope.theme[definition.name]
        if (value) variables[definition.key] = value
    })

    Object.keys(scope.elements).forEach(key => {
        if (scope.elements[key]) variables[key] = scope.elements[key]
    })

    return variables
}

function resolveScope(saved: SettingsDocument, mode: ThemeMode, light: ResolvedScope): ResolvedScope {
    const theme: BrandColors = {}

    ThemeColorDefinitions.forEach(definition => {
        const stored = storedFor(saved, 'theme', definition.name, mode)

        theme[definition.name] = stored
            ? stored
            : mode === 'dark'
                ? definition.darkDefault ?? light.theme[definition.name]
                : definition.default
    })

    const elements: BrandColors = {}

    // Resolved on demand because a colour may default to another one, which has to be settled
    // first. The set guards against a definition pointing back at itself through a chain.
    const resolving = new Set<string>()

    const resolve = (key: string): string => {
        if (elements[key] !== undefined) return elements[key]
        if (resolving.has(key)) return ''

        const definition = BrandColorDefinitions.find(d => d.key === key)
        if (!definition) return ''

        resolving.add(key)

        const stored = mode === 'dark'
            ? storedFor(saved, 'elements', key, 'dark') || storedFor(saved, 'elements', key, 'light')
            : storedFor(saved, 'elements', key, 'light')

        const reference = themeRefName(stored) ?? (stored ? null : definition.defaultRef)

        // A reference resolves against the theme of the mode being built, so setting a dark
        // theme colour reaches every element pointing at it, with no per-element dark value.
        elements[key] = reference ? theme[reference]
            : stored ? stored
                : definition.defaultOf ? resolve(definition.defaultOf)
                    : definition.defaultContrastOf ? labelOn(resolve(definition.defaultContrastOf))
                        : mode === 'dark' ? definition.darkDefault ?? definition.default
                            : definition.default

        resolving.delete(key)

        return elements[key]
    }

    BrandColorDefinitions.forEach(definition => resolve(definition.key))

    return { theme, elements }
}

/**
 * Black or white over a background, by the same brightness rule Ionic applies to a button
 * label, so a colour split out of a button keeps the label it already had.
 */
function labelOn(background: string): string {
    const hex = background?.replace('#', '')
    if (hex?.length !== 6) return '#ffffff'

    const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16))

    return Math.round((r * 299 + g * 587 + b * 114) / 1000) < 138 ? '#ffffff' : '#000000'
}

function storedFor(saved: SettingsDocument, section: 'theme' | 'elements', key: string, mode: ThemeMode): string {
    return saved?.[section]?.[mode]?.[key] || ''
}
